import React, { useState, useEffect } from "react";
import {
    useItemsFine,
    useCreateItemMutation,
    useSingleItem,
    useUpdateItemMutation,
} from "../../../_actions/app/items";
import styles from "./style/ProductManager.module.css";
import { getImageURL } from "../../../_helpers/image";
import { Button, Form, Modal, message, Input, InputNumber, Upload } from "antd";
import {
    ArrowRightOutlined,
    PlusOutlined,
    UploadOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { useUploadFile } from "../../../_actions/files";
import PhotoButton from "../../../qforms/standard_elements/PhotoButton";
import PhotoButtonMinimal from "../../../qforms/standard_elements/PhotoButtonMinimal";
import { getPublicImageURL } from "../../../_helpers/image";

function ProductManager({ org }) {
    const { isLoading, isSuccess, data } = useItemsFine(
        "qdb_product",
        ``,
        true
    );
    const [editingProduct, setEditingProduct] = useState(null);

    return (
        <div>
            <h4>Manage Products and Services</h4>
            <ProductAddModal
                trigger={<Button icon={<PlusOutlined />}>Add Product</Button>}
                editItem={editingProduct}
                onClose={() => setEditingProduct(null)}
                org={org}
            />
            {isSuccess && (
                <div className={styles.product_grid}>
                    {data.map((item) => (
                        <ProductCard
                            key={item.id}
                            item={item}
                            onClick={() => setEditingProduct(item)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default ProductManager;

function ProductCard({ item, onClick }) {
    return (
        <div
            className={styles.item_wrapper}
            onClick={onClick}
            style={{ cursor: "pointer" }}
        >
            <img src={getImageURL(item.main_image)} />
            <p>{item.name}</p>
        </div>
    );
}

function ProductAddModal({
    trigger,
    editItem = null,
    onClose = () => {},
    org,
}) {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [additionalImages, setAdditionalImages] = useState([]);
    console.log("org", org);

    const isEditMode = !!editItem;

    // Fetch full product data when editing
    const {
        data: fullProductData,
        isSuccess: fetchSuccess,
        isLoading: fetchLoading,
    } = useSingleItem(
        "qdb_product",
        `/${editItem?.id}?fields=*,images.*`,
        editItem !== null
    );

    const createMutation = useCreateItemMutation(
        () => {
            message.success("Product added successfully!");
            setOpen(false);
            form.resetFields();
            setAdditionalImages([]);
        },
        (error) => {
            message.error("Failed to add product. Please try again.");
            console.error("Create product error:", error);
        }
    );

    const updateMutation = useUpdateItemMutation(
        () => {
            message.success("Product updated successfully!");
            setOpen(false);
            form.resetFields();
            setAdditionalImages([]);
            onClose();
        },
        (error) => {
            message.error("Failed to update product. Please try again.");
            console.error("Update product error:", error);
        }
    );

    const fileMutation = useUploadFile((data) => {
        console.log("Additional image uploaded:", data);
        // Store in directus_files_id format
        const newImages = [...additionalImages, { directus_files_id: data.id }];
        setAdditionalImages(newImages);
    });

    // Load existing product data into form when in edit mode
    useEffect(() => {
        if (isEditMode && fetchSuccess && fullProductData) {
            // Set form values
            form.setFieldsValue({
                name: fullProductData.name,
                description: fullProductData.description,
                key_highlights: fullProductData.key_highlights,
                price: fullProductData.price,
                extra: fullProductData.extra,
                main_image: fullProductData.main_image,
            });

            // Set additional images if they exist
            if (
                fullProductData.images &&
                Array.isArray(fullProductData.images)
            ) {
                setAdditionalImages(fullProductData.images);
            }
        }
    }, [isEditMode, fetchSuccess, fullProductData, form]);

    // Open modal when editItem changes
    useEffect(() => {
        if (editItem) {
            setOpen(true);
        }
    }, [editItem]);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            if (isEditMode) {
                onClose();
            }
            form.resetFields();
            setAdditionalImages([]);
        }
    }, [open]);

    // Sync additionalImages with form field
    useEffect(() => {
        form.setFieldsValue({ images: additionalImages });
    }, [additionalImages, form]);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const productData = {
                ...values,
                organization: org,
            };

            if (isEditMode) {
                // Update existing product
                updateMutation.mutate({
                    collection: "qdb_product",
                    id: editItem.id,
                    data: productData,
                });
            } else {
                // Create new product
                createMutation.mutate({
                    collection: "qdb_product",
                    data: productData,
                });
            }
        });
    };

    const handleAddMoreClick = () => {
        document.getElementById("additional-images-upload").click();
    };

    const onFileChange = (eve) => {
        const file = eve.target.files[0];
        if (file) {
            if (typeof file === "object" && file !== null) {
                const formData = new FormData();
                formData.append("file", file);
                fileMutation.mutate(formData);
            }
        }
        // Reset input value so same file can be selected again
        eve.target.value = "";
    };

    const removeImage = (indexToRemove) => {
        const newImages = additionalImages.filter(
            (_, index) => index !== indexToRemove
        );
        setAdditionalImages(newImages);
    };

    const handleClose = () => {
        setOpen(false);
        if (isEditMode) {
            onClose();
        }
    };

    return (
        <>
            {!isEditMode && (
                <span onClick={() => setOpen(true)}>{trigger}</span>
            )}
            <Modal
                open={open}
                onCancel={handleClose}
                width="900px"
                title={isEditMode ? "Edit Product" : "Add New Product"}
                footer={[
                    <Button onClick={handleClose}>Cancel</Button>,
                    <Button
                        type="primary"
                        loading={
                            createMutation.isLoading || updateMutation.isLoading
                        }
                        onClick={handleSubmit}
                        icon={<ArrowRightOutlined />}
                        iconPosition="end"
                    >
                        {isEditMode ? "Update Product" : "Add Product"}
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            marginTop: "20px",
                        }}
                    >
                        <div>
                            <Form.Item label="Product Photo" name="main_image">
                                <PhotoButton
                                    field={{
                                        collection: "qqq",
                                        field: "main_image",
                                    }}
                                    width="320px"
                                    height="320px"
                                    objectFit="contain"
                                    show_placeholder={true}
                                    show_choose={true}
                                />
                            </Form.Item>

                            {/* Additional Images Preview */}
                            {additionalImages.length > 0 && (
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "10px",
                                        marginBottom: "10px",
                                    }}
                                >
                                    {additionalImages.map((imageObj, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                position: "relative",
                                                width: "80px",
                                                height: "80px",
                                            }}
                                        >
                                            <img
                                                src={getPublicImageURL(
                                                    imageObj.directus_files_id
                                                )}
                                                alt={`preview-${index}`}
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    borderRadius: "4px",
                                                    border: "1px solid #d9d9d9",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                icon={<CloseOutlined />}
                                                onClick={() =>
                                                    removeImage(index)
                                                }
                                                style={{
                                                    position: "absolute",
                                                    top: "-8px",
                                                    right: "-8px",
                                                    width: "20px",
                                                    height: "20px",
                                                    padding: "0",
                                                    minWidth: "20px",
                                                    borderRadius: "50%",
                                                    background: "white",
                                                    border: "1px solid #ff4d4f",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "10px",
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Button
                                type="dashed"
                                onClick={handleAddMoreClick}
                                loading={fileMutation.isLoading}
                                icon={<PlusOutlined />}
                            >
                                {fileMutation.isLoading
                                    ? "Uploading..."
                                    : "Add More"}
                            </Button>

                            {/* Hidden input for additional images */}
                            <input
                                type="file"
                                id="additional-images-upload"
                                onChange={onFileChange}
                                hidden
                                style={{ display: "none" }}
                                accept="image/*"
                            />

                            {/* Hidden form field to store images array */}
                            <Form.Item name="images" hidden>
                                <Input />
                            </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Form.Item
                                name="name"
                                label="Name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter product name",
                                    },
                                ]}
                            >
                                <Input placeholder="Enter product name" />
                            </Form.Item>

                            <Form.Item name="description" label="Description">
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Enter product description"
                                />
                            </Form.Item>

                            <Form.Item
                                name="key_highlights"
                                label="Key Highlights"
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Enter key highlights"
                                />
                            </Form.Item>

                            <Form.Item name="price" label="Price (Optional)">
                                <InputNumber
                                    placeholder="Enter price"
                                    style={{ width: "100%" }}
                                    min={0}
                                    step={0.01}
                                />
                            </Form.Item>
                            <Form.Item name="extra" label="Extra">
                                <Input.TextArea
                                    rows={1}
                                    placeholder="Enter extra information"
                                />
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            </Modal>
        </>
    );
}
